import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { StackingComponentProps } from '../../utils/StackingComponent';
import { ToastBaseProps, ToastType } from './ToastBase';
import { Toaster, ToastId } from './Toaster';

export { ToastType };

export interface ToastProps extends ToastBaseProps, Pick<StackingComponentProps, 'zIndex'> {
  /**
   * The following scenarios never auto-dismiss regardless of this prop's value:
   * * Error/Warning Toasts
   * * Toasts with an action link/message
   * @default true
   */
  autoDismiss?: boolean;
  /**
   * time in milliseconds after which Toast will dismiss
   * @default 5000
   */
  autoDismissTimeMS?: number;
  /** @deprecated has no effect */
  topLeftXPx?: number;
  /** @deprecated has no effect */
  topLeftYPx?: number;
}

export class ToastWidget extends React.PureComponent<ToastProps> {
  private static toasterRef: React.RefObject<Toaster> = React.createRef();
  private static toasterDiv: HTMLDivElement | undefined;

  /**
   * This must be called in the application initializer before any of the other imperative APIs may be called.
   */
  public static initToaster(toasterStyle?: React.CSSProperties, callback?: () => void): void {
    if (!ToastWidget.toasterRef.current) {
      ToastWidget.toasterDiv = document.createElement('div');
      ToastWidget.toasterDiv.classList.add('tab-shared-widget-toaster');
      document.body.appendChild(ToastWidget.toasterDiv);
      ReactDOM.render(<Toaster ref={ToastWidget.toasterRef} style={toasterStyle} />, ToastWidget.toasterDiv, callback);
    } else {
      callback?.();
    }
  }

  /**
   * This will update the styles applied to the `Toaster` element
   */
  public static updateToasterStyle(toasterStyle: React.CSSProperties): void {
    if (ToastWidget.toasterRef.current && ToastWidget.toasterDiv) {
      ReactDOM.render(<Toaster ref={ToastWidget.toasterRef} style={toasterStyle} />, ToastWidget.toasterDiv);
    }
  }

  /**
   * Shows a new Toast for the specified props.
   * Most use cases will want to use this imperative API, rather than a declarative `<ToastWidget>` in a render method.
   * @returns an id which may optionally be retained for later use with the `ToastWidget.dismiss` or `ToastWidget.update` methods
   */
  public static show(props: ToastProps): ToastId {
    return ToastWidget.toasterRef.current!.show(props);
  }

  /**
   * Updates an existing Toast with new props.
   * @param toastId the id previously returned by the `ToastWidget.show` method
   * @param props the updated `ToastProps` to replace in the Toast
   */
  public static update(toastId: ToastId, props: Partial<ToastProps>): void {
    ToastWidget.toasterRef.current!.update(toastId, props);
  }

  /**
   * Immediately dismisses a currently-shown Toast.
   * @param toastId the id previously returned by the `ToastWidget.show` method
   */
  public static dismiss(toastId: ToastId): void {
    ToastWidget.toasterRef.current!.dismiss(toastId);
  }

  /*
   * below is the shim to the original declarative API, which utilizes lifecycle methods to invoke the imperative API
   * in lieu of declarative rendering
   */

  private toastId?: ToastId = undefined;

  /**
   * This method gets called when a declaratively instantiated Toast gets dismissed.
   * It prevents ToastWidget.dismiss() from being called (a 2nd time) in `componentWillUnmount` if the ToastWidget has already dismissed.
   */
  private declarativeCleanup = (): void => {
    this.toastId = undefined;
    this.props.onCloseRequest?.();
  }

  public componentDidMount(): void {
    const addToast = () => this.toastId = ToastWidget.show({ ...this.props, onCloseRequest: this.declarativeCleanup });
    if (!ToastWidget.toasterRef.current) {
      ToastWidget.initToaster({}, addToast);
    } else {
      addToast();
    }
  }

  public componentDidUpdate(): void {
    ToastWidget.update(this.toastId!, this.props);
  }

  public componentWillUnmount(): void {
    if (!!this.toastId) {
      ToastWidget.dismiss(this.toastId!);
      this.toastId = undefined;
    }
  }

  public render(): null { return null; }
}
